import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, Map, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";
import { useAuth } from "../../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHAPE = { rounded: "rounded-xl", pill: "rounded-full", square: "rounded-md" };
const SPACING = { cozy: "space-y-4", compact: "space-y-2.5", roomy: "space-y-6" };

const parseDt = (d, t) => {
  if (!d) return null;
  const dt = new Date(t ? `${d}T${t}` : d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const STD_KEYS = ["name", "email", "phone", "tour", "vehicle", "duration_option", "guests",
  "pickup_date", "pickup_time", "return_date", "return_time", "pickup_location", "dropoff_location", "message"];

export const BookingModal = ({ state, onClose }) => {
  const { tours, fleet, fmtPrice, settings, locale } = useSiteData();
  const { user } = useAuth();
  const rtl = locale === "ar";
  const offer = state.offer || null;

  const [cfg, setCfg] = useState(null);
  const [values, setValues] = useState({});
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const overlayRef = useRef(null);
  const panelRef = useRef(null);

  const tt = (f, key) => (rtl && f[`${key}_ar`]) ? f[`${key}_ar`] : f[`${key}_en`] || "";

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/booking-form`).then((r) => {
      if (!alive) return;
      const c = r.data;
      const init = {};
      (c.fields || []).forEach((f) => {
        if (f.standard_key === "name") init[f.id] = user?.name || "";
        else if (f.standard_key === "email") init[f.id] = user?.email || "";
        else if (f.type === "guests" || f.standard_key === "guests") init[f.id] = f.default || "2";
        else init[f.id] = f.default || "";
      });
      const tourField = (c.fields || []).find((f) => f.type === "tour");
      if (tourField) {
        if (offer?.tours?.length) init[tourField.id] = offer.tours[0];
        else if (state.tour) init[tourField.id] = state.tour.name;
      }
      setValues(init);
      setCfg(c);
    }).catch(() => setCfg({ enabled: true, submissions_enabled: true, layout: "single", fields: [], design: {} }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Take over wheel handling so the form scrolls (a page script hijacks wheel).
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const onWheel = (e) => {
      const panel = panelRef.current;
      if (panel && panel.contains(e.target)) {
        panel.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    overlay.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => overlay.removeEventListener("wheel", onWheel, { capture: true });
  }, []);

  // Lock background scroll + hide the floating chat button while open (mobile overlap fix).
  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.overflow = "hidden";
    body.classList.add("booking-open");
    return () => {
      body.style.position = ""; body.style.top = ""; body.style.left = "";
      body.style.right = ""; body.style.overflow = "";
      body.classList.remove("booking-open");
      window.scrollTo(0, scrollY);
    };
  }, []);

  const design = cfg?.design || {};
  const shape = SHAPE[design.field_shape] || SHAPE.rounded;
  const accent = design.accent_color || "var(--brand)";
  const inputCls = `w-full ${shape} border border-slate-200 bg-white px-4 py-3 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20`;

  const fields = cfg?.fields || [];
  const set = (id, v) => setValues((p) => ({ ...p, [id]: v }));

  const isVisible = (f) => {
    if (f.hidden) return false;
    const c = f.conditional;
    if (c && c.field) return String(values[c.field] ?? "") === String(c.value ?? "");
    return true;
  };

  // Map field values to standard enquiry keys + custom fields.
  const mapped = useMemo(() => {
    const std = {}; const custom = {};
    fields.forEach((f) => {
      const v = values[f.id];
      if (f.standard_key && STD_KEYS.includes(f.standard_key)) std[f.standard_key] = v;
      else if (isVisible(f) && v !== "" && v != null) custom[f.id] = v;
    });
    return { std, custom };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, fields]);

  // ---- Price summary (server re-validates; offer price always wins) ----
  const selectedTour = tours.find((t) => t.name === mapped.std.tour);
  const selectedVehicle = fleet.find((v) => v.name === mapped.std.vehicle);
  const halfPrice = Number(settings.half_day_price) || 0;
  const fullPrice = Number(settings.full_day_price) || 0;
  const start = parseDt(mapped.std.pickup_date, mapped.std.pickup_time);
  const end = parseDt(mapped.std.return_date, mapped.std.return_time);
  let days = start && end && end > start ? Math.max(1, Math.ceil((end - start) / 86400000)) : null;
  let pricing = null;
  if (offer) {
    pricing = { flat: true, label: offer.title_en || "Special offer", total: Number(offer.offer_price) || 0 };
  } else if (mapped.std.duration_option) {
    const opt = mapped.std.duration_option === "half_day" ? { label: "Half Day", price: halfPrice } : { label: "Full Day", price: fullPrice };
    pricing = { flat: true, label: opt.label, total: opt.price };
  } else if (selectedVehicle) {
    const qty = days || 1;
    pricing = { unitLabel: "per day", unit: selectedVehicle.price, qtyLabel: `${qty} day${qty > 1 ? "s" : ""}`, total: qty * selectedVehicle.price };
  } else if (selectedTour) {
    const unit = selectedTour.sale_price || selectedTour.price;
    const qty = Math.max(1, parseInt(mapped.std.guests, 10) || 1);
    pricing = { unitLabel: "per guest", unit, qtyLabel: `${qty} guest${qty > 1 ? "s" : ""}`, total: qty * unit };
  }

  // ---- Steps ----
  const multistep = cfg?.layout === "multistep" && (cfg?.steps?.length || 0) > 1;
  const steps = multistep ? cfg.steps : [{ id: "all" }];
  const stepFields = multistep ? fields.filter((f) => (f.step || 0) === step) : fields;
  const isLastStep = step >= steps.length - 1;

  const validateStep = (list) => {
    for (const f of list) {
      if (isVisible(f) && f.required && String(values[f.id] ?? "").trim() === "") {
        toast.error(`Please fill in: ${tt(f, "label") || f.id}`);
        return false;
      }
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep(fields)) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...mapped.std,
        custom_fields: mapped.custom,
        offer_id: offer?.id || null,
        type: "tour_booking",
      });
      const tourName = mapped.std.tour || (offer?.title_en) || "Your tour";
      toast.success(`${tourName} ${tt(design, "success") || design.success_en || "booked — details are on the way to your inbox."}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete the booking. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const whatsappHref = () => {
    const num = (settings.whatsapp || settings.contact_phone || "").replace(/[^\d]/g, "");
    const msg = encodeURIComponent(`Hello Voyage Swiss! I'd like to ask about booking ${mapped.std.tour || offer?.title_en || "a tour"}.`);
    return `https://wa.me/${num}?text=${msg}`;
  };

  // ---- Field renderer ----
  const renderField = (f) => {
    if (!isVisible(f)) return null;
    const label = tt(f, "label");
    const ph = tt(f, "placeholder");
    const common = { "data-testid": `bf-field-${f.id}`, value: values[f.id] ?? "", className: inputCls };
    let control;
    switch (f.type) {
      case "tour":
        control = (
          <select {...common} onChange={(e) => set(f.id, e.target.value)} className={`${inputCls} ${values[f.id] ? "text-[#0b132b]" : "text-slate-400"}`}>
            <option value="">{ph || "Select a tour…"}</option>
            {tours.map((t) => <option key={t.id} value={t.name}>{`${t.name} — from ${fmtPrice(t.sale_price || t.price)}`}</option>)}
          </select>
        );
        break;
      case "vehicle":
        control = (
          <select {...common} onChange={(e) => set(f.id, e.target.value)} className={`${inputCls} ${values[f.id] ? "text-[#0b132b]" : "text-slate-400"}`}>
            <option value="">{ph || "We'll arrange the right vehicle"}</option>
            {fleet.map((v) => <option key={v.id} value={v.name}>{`${v.name} — ${fmtPrice(v.price)}/day`}</option>)}
          </select>
        );
        break;
      case "duration":
        control = (
          <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0" data-testid={`bf-field-${f.id}`}>
            {[{ key: "half_day", label: "Half Day", price: halfPrice }, { key: "full_day", label: "Full Day", price: fullPrice }].map((o) => (
              <button key={o.key} type="button" data-testid={`bf-duration-${o.key}`} onClick={() => set(f.id, o.key)}
                className={`${SHAPE[design.field_shape] || "rounded-2xl"} border-2 px-4 py-3 text-left transition-all ${values[f.id] === o.key ? "border-[var(--brand)] bg-[var(--brand-soft)] shadow-md" : "border-slate-200 bg-white hover:border-[var(--brand)]/40"}`}>
                <span className={`block text-sm font-bold ${values[f.id] === o.key ? "text-[var(--brand)]" : "text-[#0b132b]"}`}>{o.label}</span>
                <span className="block text-xs font-semibold text-slate-500 mt-0.5">{fmtPrice(o.price)}</span>
              </button>
            ))}
          </div>
        );
        break;
      case "guests":
        control = (
          <select {...common} onChange={(e) => set(f.id, e.target.value)}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={String(n)}>{n} {n === 1 ? "guest" : "guests"}</option>)}
          </select>
        );
        break;
      case "select":
        control = (
          <select {...common} onChange={(e) => set(f.id, e.target.value)} className={`${inputCls} ${values[f.id] ? "text-[#0b132b]" : "text-slate-400"}`}>
            <option value="">{ph || "Select…"}</option>
            {(f.options || []).map((o, i) => <option key={i} value={o.value}>{(rtl && o.label_ar) ? o.label_ar : o.label_en || o.value}</option>)}
          </select>
        );
        break;
      case "checkbox":
        control = (
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid={`bf-field-${f.id}`} checked={!!values[f.id]} onChange={(e) => set(f.id, e.target.checked)} className="w-5 h-5 accent-[var(--brand)]" />
            <span className="text-sm font-medium text-[#0b132b]">{ph || label}</span>
          </label>
        );
        break;
      case "textarea":
        control = <textarea {...common} rows={3} placeholder={ph} onChange={(e) => set(f.id, e.target.value)} className={`${inputCls} resize-none`} />;
        break;
      default:
        control = <input {...common} type={f.type === "tel" ? "tel" : f.type} placeholder={ph} onChange={(e) => set(f.id, e.target.value)} />;
    }
    return (
      <label className={`block ${f.width === "half" ? "" : "sm:col-span-2"}`} key={f.id}>
        {f.type !== "checkbox" && (
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
            {label}{f.required && <span className="text-[var(--brand)]"> *</span>}
          </span>
        )}
        <div className={f.type === "checkbox" ? "" : "mt-1"}>{control}</div>
      </label>
    );
  };

  const disabled = cfg && cfg.submissions_enabled === false;

  return (
    <motion.div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-[#0b132b]/50 backdrop-blur-sm overflow-hidden overscroll-none"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} dir={rtl ? "rtl" : "ltr"} data-testid="booking-modal-overlay"
    >
      <motion.div
        ref={panelRef}
        initial={{ opacity: 0, y: 40, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg max-h-[92dvh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden overscroll-contain [touch-action:pan-y] rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl"
        data-testid="booking-modal"
      >
        {/* Sticky header keeps the close button always reachable */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 bg-white/95 backdrop-blur px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand)] truncate">{tt(design, "subtitle") || "Voyage Switzerland"}</p>
            <h3 className="mt-1 font-display font-bold text-xl sm:text-2xl text-[#0b132b] flex items-center gap-2">
              <Map className="w-5 h-5 text-[var(--brand)] shrink-0" /> {tt(design, "title") || "Book a Tour"}
            </h3>
            {offer && <p className="mt-1 text-xs font-semibold text-emerald-600" data-testid="booking-offer-tag">Offer applied: {offer.title_en} · {fmtPrice(offer.offer_price)}</p>}
          </div>
          <button data-testid="booking-close-button" onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 sm:px-8 pb-7 pt-5">
          {!cfg ? (
            <div className="py-16 text-center text-sm text-slate-400" data-testid="booking-loading">Loading…</div>
          ) : disabled ? (
            <div className="py-8 text-center space-y-4" data-testid="booking-disabled">
              <p className="text-sm text-slate-600">Online booking is temporarily closed. Please reach us directly and we'll help you right away.</p>
              {cfg.whatsapp_enabled !== false && (
                <a href={whatsappHref()} target="_blank" rel="noreferrer" data-testid="booking-whatsapp-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white text-sm font-bold px-6 py-3">
                  <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                </a>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className={SPACING[design.spacing] || SPACING.cozy} data-testid="booking-form">
              {multistep && (
                <div className="flex items-center gap-2 mb-1" data-testid="booking-steps">
                  {steps.map((s, i) => (
                    <div key={s.id} className={`flex-1 h-1.5 rounded-full ${i <= step ? "bg-[var(--brand)]" : "bg-slate-200"}`} />
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>*]:min-w-0">
                {stepFields.map(renderField)}
              </div>

              {cfg.show_price_summary !== false && pricing && (
                <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="booking-price-summary">
                  {days != null && !pricing.flat && (
                    <div><p className="text-2xl font-display font-extrabold text-[var(--brand)]">{days}</p><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">day{days > 1 ? "s" : ""}</p></div>
                  )}
                  <div>
                    <p className="text-sm font-bold text-[#0b132b]" data-testid="booking-unit-price">
                      {pricing.flat ? `${pricing.label}` : `${fmtPrice(pricing.unit)} ${pricing.unitLabel} × ${pricing.qtyLabel}`}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{offer ? "offer price" : "auto-calculated"}</p>
                  </div>
                  <div><p className="text-2xl font-display font-extrabold text-[#0b132b]" data-testid="booking-total-price">{fmtPrice(pricing.total)}</p><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">total price</p></div>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
                {cfg.whatsapp_enabled !== false && (settings.whatsapp || settings.contact_phone) && (
                  <a href={whatsappHref()} target="_blank" rel="noreferrer" data-testid="booking-whatsapp-button"
                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#25D366] text-[#128C7E] text-sm font-bold px-5 py-3.5 hover:bg-[#25D366]/5 transition">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                )}
                {multistep && !isLastStep ? (
                  <button type="button" data-testid="booking-next-button" onClick={() => { if (validateStep(stepFields)) setStep((s) => s + 1); }}
                    className="flex-1 rounded-full text-white text-sm font-bold py-3.5 shadow-lg transition hover:brightness-110" style={{ backgroundColor: accent }}>
                    Continue
                  </button>
                ) : (
                  <button type="submit" data-testid="booking-submit-button" disabled={sending}
                    className="flex-1 rounded-full text-sm font-bold py-3.5 shadow-lg transition hover:brightness-110 disabled:opacity-60"
                    style={{ backgroundColor: accent, color: design.button_text_color || "#fff" }}>
                    {sending ? "Booking…" : (tt(design, "submit_text") || "Confirm Tour Booking")}
                  </button>
                )}
                {multistep && step > 0 && (
                  <button type="button" data-testid="booking-back-button" onClick={() => setStep((s) => s - 1)}
                    className="rounded-full border-2 border-slate-200 text-slate-600 text-sm font-bold px-5 py-3.5 hover:border-slate-300 transition">Back</button>
                )}
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
